import React, { useState, useEffect, useRef } from "react";

const THUMBNAIL_TIMEOUT_MS = 8000;

const withThumbnailTimeout = (promise) => {
    let id;
    const timeout = new Promise((_, reject) => {
        id = setTimeout(() => reject(new Error("Thumbnail timeout")), THUMBNAIL_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(id));
};

class ThumbnailQueue {
    constructor() {
        this.queue = [];
        this.processing = false;
    }

    enqueue(fo, checkActive, isShared = false) {
        return new Promise((resolve, reject) => {
            this.queue.push({ fo, resolve, reject, checkActive, isShared });
            this.processNext();
        });
    }

    async processNext() {
        if (this.processing || this.queue.length === 0) return;
        this.processing = true;

        while (this.queue.length > 0) {
            const { fo, resolve, reject, checkActive, isShared } = this.queue.shift();
            if (!checkActive()) {
                resolve(null);
                continue;
            }
            try {
                const bytes = await withThumbnailTimeout(fo.getThumbnail(!isShared));
                resolve(bytes);
            } catch (e) {
                resolve(null); // resolve with null so the queue keeps moving; caller shows default icon
            }
        }
        this.processing = false;
    }
}

const globalThumbnailQueue = new ThumbnailQueue();

const GridThumbnail = ({ fo, defaultIcon, isShared }) => {
    const [thumbUrl, setThumbUrl] = useState(null);
    const imgRef = useRef(null);

    useEffect(() => {
        if (!fo || typeof fo.getThumbnail !== 'function') return;

        let fileType = "";
        let fileName = "";
        try {
            if (typeof fo.getFileType === 'function') fileType = fo.getFileType()?.toLowerCase() || "";
            if (typeof fo.getName === 'function') fileName = fo.getName()?.toLowerCase() || "";
        } catch (e) {
            console.warn("Could not determine file type/name for thumbnail", e);
        }

        const isSupportedType =
            fileType.startsWith("image/") ||
            fileType.startsWith("video/") ||
            fileType === "application/pdf" ||
            (/\.(jpg|jpeg|png|gif|svg|webp|heic|heif|mp4|webm|ogv|mov|mkv|pdf)$/i.test(fileName));

        if (!isSupportedType) return;

        let active = true;
        let timeoutId = null;
        let hasFetched = false;

        const observer = new IntersectionObserver((entries) => {
            const entry = entries[0];
            if (entry.isIntersecting) {
                if (!hasFetched) {
                    timeoutId = setTimeout(() => {
                        if (!active || hasFetched) return;
                        hasFetched = true;
                        globalThumbnailQueue.enqueue(fo, () => active, isShared).then((bytes) => {
                            if (!active || !bytes) return;
                            if (bytes && bytes.length > 0) {
                                const blob = new Blob([bytes], { type: "image/jpeg" });
                                setThumbUrl(URL.createObjectURL(blob));
                            }
                        }).catch(err => {
                            console.warn("Error loading thumbnail:", err);
                        });
                    }, 400); // 400ms debounce
                }
            } else {
                if (timeoutId) {
                    clearTimeout(timeoutId);
                    timeoutId = null;
                }
            }
        }, {
            root: null,
            rootMargin: "0px",
            threshold: 0.1
        });

        if (imgRef.current) observer.observe(imgRef.current);

        return () => {
            active = false;
            if (timeoutId) clearTimeout(timeoutId);
            if (imgRef.current) observer.unobserve(imgRef.current);
        };
    }, [fo, isShared]);

    useEffect(() => {
        return () => {
            if (thumbUrl) URL.revokeObjectURL(thumbUrl);
        };
    }, [thumbUrl]);

    return (
        <img
            ref={imgRef}
            src={thumbUrl || defaultIcon}
            alt="file icon"
            className="fileIcon"
            style={thumbUrl ? { objectFit: "cover", width: "100%", height: "100%", borderRadius: "8px" } : {}}
        />
    );
};

export default GridThumbnail;
